import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import { CSSProperties } from "@mui/styles";
import CircularProgress from "@mui/material/CircularProgress";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import TextField from "@mui/material/TextField";
import { asyncDebounce } from "./helpers";

type UiState =
    | "pre_init"
    | "loading_initial_value"
    | "no_selection"
    | "input_changed"
    | "searching"
    | "showing_options"
    | "selection"
    | "error";

export type SelectorProps<TId, TEntity> = {
    initialId?: TId;
    id?: string;
    style?: CSSProperties;
    smallSize?:boolean;
    localisation: {
        loadingLabel: string;
        label: string;
        noOptionsLabel: string;
        placeholder?: string;
        helperText?: string;
        errorText?: string;
    };
    searchDebounceDelay?: number;
    required?: boolean;
    onSelected: (entity: TEntity | null) => void;
    search: (searchTerm: string) => Promise<TEntity[]>;
    getById?: (id: TId) => Promise<TEntity>;
    render: (entity: TEntity) => string;
    getIdOf?: (entity: TEntity) => TId;
    isValid?: (valid: boolean) => void;
};

function useCheckMemoisation(fn: Function | undefined | null, message?: string): void {
    const ref = useRef(fn);
    if (ref.current !== fn) {
        console.debug("Original Callback: ", ref.current, "New Callback", fn);
        throw new Error(
            "Selector Component: Callback function has changed implementation without being memoized. " +
                message +
                "\nOld:" +
                ref.current?.toString() +
                "\nNew:" +
                fn?.toString()
        );
    }
}

var numberOfSelectors: number = 1;

function Selector<TId, TEntity>(props: PropsWithChildren<SelectorProps<TId, TEntity>>) {
    const { id, style, initialId, localisation, searchDebounceDelay, required, smallSize } = props;

    const [uiState, setUiState] = useState<UiState>("pre_init");
    const [error, setError] = useState<Error | null>(null);
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([] as TEntity[]);
    const [selected, setSelected] = useState<TEntity | null>(null);
    const [showError, setShowError] = useState<boolean>(false);

    const { getIdOf, search, getById, render, onSelected, isValid } = props;
    useCheckMemoisation(getIdOf, "getIdOf");
    useCheckMemoisation(search, "search");
    useCheckMemoisation(getById, "getById");
    useCheckMemoisation(render, "render");
    useCheckMemoisation(onSelected, "onSelected");

    const searchDebounced = useMemo(
        () => asyncDebounce(search, searchDebounceDelay ?? 300),
        [search, searchDebounceDelay]
    );

    function checkIdEquality(left: TEntity, right: TEntity): boolean {
        const defaultGetIdOf = (entity: TEntity): TId => (entity as any)?.id;
        const getId = getIdOf ?? defaultGetIdOf;
        return getId(left) === getId(right);
    }

    const selectorIndex = useMemo(() => ++numberOfSelectors, []);

    useEffect(() => {
        switch (uiState) {
            case "pre_init":
                setUiState("no_selection");
                if (initialId && getById) {
                    setError(null);
                    setOptions([]);
                    isValid?.(true);
                    setUiState("loading_initial_value");
                    getById(initialId)
                        .then((item) => {
                            setOptions([item]);
                            onSelected(item);
                            setSelected(item);
                            setUiState("selection");
                            setInputValue(render(item));
                        })
                        .catch((error) => {
                            setError(error);
                            setOptions([]);
                            setUiState("error");
                            setInputValue("");
                        });
                }
                break;
            case "input_changed":
                setError(null);
                setOptions([]);
                setUiState("searching");
                searchDebounced(inputValue)
                    .then((results) => {
                        setOptions(results);
                        setUiState("showing_options");
                    })
                    .catch((error) => {
                        setError(error);
                        setOptions([]);
                        setUiState("error");
                    });
                break;
            default:
                break;
        }
    }, [uiState, inputValue, initialId, getById, onSelected, render, searchDebounced]);

    const { loadingLabel, label, noOptionsLabel, helperText, errorText, placeholder } = localisation;

    const isBusy = uiState === "loading_initial_value" || uiState === "searching";
    const isOpen = uiState === "searching" || uiState === "showing_options";
    const labelText = uiState === "loading_initial_value" ? loadingLabel : label;

    return (
        <Autocomplete
            id={id ?? `selector-${selectorIndex}`}
            sx={style}
            size = {smallSize === true ? "small" : "medium"}
            selectOnFocus={true}
            handleHomeEndKeys={false}
            open={isOpen}
            onClose={(_, reason) => {
                setUiState(reason === "selectOption" ? "selection" : "no_selection");
            }}
            onChange={(_, newValue, reason) => {
                if (reason === "selectOption") {
                    onSelected(newValue);
                    setSelected(newValue);
                    setUiState("selection");
                    setShowError(false);
                    isValid?.(true);
                } else if (reason === "blur" || reason === "clear") {
                    onSelected(null);
                    setSelected(null);
                    setUiState("no_selection");
                }
            }}
            isOptionEqualToValue={checkIdEquality}
            getOptionLabel={render}
            options={options}
            loading={isBusy}
            filterOptions={(options, _) => options}
            noOptionsText={noOptionsLabel}
            loadingText={loadingLabel}
            inputValue={inputValue || ""}
            value={selected}
            onInputChange={(evt, newInputValue, reason) => {

                setInputValue(newInputValue || "");

                if (reason !== "reset") {
                    setUiState("input_changed");
                }
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    required={required}
                    error={showError}
                    onFocus={() => {
                        setShowError(false);
                    }}
                    onBlur={() => {
                        if (selected === null && required) {
                           setShowError(true);
                           isValid?.(false);
                        }

                        if (selected !== null && required) {
                            isValid?.(true);
                         }
                    }}
                    placeholder={placeholder}
                    label={labelText}
                    variant="outlined"
                    helperText={showError ? errorText : helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {isBusy && <CircularProgress color="inherit" size={20} aria-busy="true" />}
                                {error && <SentimentVeryDissatisfiedIcon aria-label="error-status" />}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
        />
    );
}

export default Selector;
